import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import axios from "axios";
import PayPalCheckout from "@/components/PayPalCheckout";
import {
  FileText,
  CreditCard,
  Search,
  CheckCircle,
  AlertCircle,
  Lock,
  Shield,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PayInvoicePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const prefilledInvoice = searchParams.get("invoice") || "";

  const [invoiceNumber, setInvoiceNumber] = useState(prefilledInvoice);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [error, setError] = useState(null);

  const searchInvoice = async () => {
    if (!invoiceNumber.trim()) {
      toast.error("Please enter an invoice number");
      return;
    }

    setLoading(true);
    setError(null);
    setInvoice(null);

    try {
      const response = await axios.get(`${API}/invoices/${invoiceNumber.trim().toUpperCase()}`);
      setInvoice(response.data);
      if (response.data.status === "paid") {
        setError("This invoice has already been paid");
      }
    } catch (err) {
      console.error("Error finding invoice:", err);
      setError("Invoice not found. Please check the invoice number and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStripePayment = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/invoices/pay`, {
        invoice_number: invoice.invoice_number,
        payment_method: "stripe",
        origin_url: window.location.origin,
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      console.error("Payment error:", err);
      toast.error("Payment failed. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const createPayPalOrder = (data, actions) => {
    const amount = Number(invoice ? invoice.amount - invoice.amount_paid : 0).toFixed(2);
    return actions.order.create({
      purchase_units: [
        {
          description: `ProToCall Invoice ${invoice?.invoice_number || ""}`,
          custom_id: invoice?.invoice_number,
          amount: {
            value: amount,
            currency_code: "USD",
          },
        },
      ],
      application_context: {
        shipping_preference: "NO_SHIPPING",
        user_action: "PAY_NOW",
      },
    });
  };

  const onPayPalApprove = async (data, actions) => {
    try {
      const details = await actions.order.capture();
      await axios.post(`${API}/payments/paypal/record`, {
        order_id: data.orderID,
        invoice_number: invoice.invoice_number,
        amount: invoice.amount - invoice.amount_paid,
        payer_email: details?.payer?.email_address || null,
      });
      toast.success("Payment successful!");
      navigate(`/payment/success?session_id=${data.orderID}&method=paypal&type=invoice`);
    } catch (err) {
      console.error("PayPal capture error:", err);
      toast.error("Payment capture failed. Please contact support if charged.");
    }
  };

  const amountDue = invoice ? invoice.amount - invoice.amount_paid : 0;

  return (
    <div className="min-h-screen bg-slate-50 py-12 md:py-20" data-testid="pay-invoice-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <span className="inline-block bg-orange-100 text-orange-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
            Invoice Payment
          </span>
          <h1
            className="text-3xl font-bold text-slate-900 mb-2"
            style={{ fontFamily: "Manrope, sans-serif" }}
            data-testid="pay-invoice-title"
          >
            Pay Your Invoice
          </h1>
          <p className="text-slate-600">
            Enter your invoice number to make a payment
          </p>
        </div>

        {/* Invoice Search */}
        <Card className="bg-white border border-slate-200 shadow-lg mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-500" />
              Find Your Invoice
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <div className="flex-1">
                <Input
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value.toUpperCase())}
                  placeholder="e.g., INV-202501-ABC123"
                  className="h-11 font-mono"
                  data-testid="invoice-number-input"
                />
              </div>
              <Button
                onClick={searchInvoice}
                disabled={loading}
                className="bg-slate-900 hover:bg-slate-800 text-white h-11 px-6"
                data-testid="search-invoice-btn"
              >
                {loading ? (
                  <span className="animate-spin">⌛</span>
                ) : (
                  <>
                    <Search className="mr-2 w-4 h-4" />
                    Find
                  </>
                )}
              </Button>
            </div>
            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Invoice Details & Payment */}
        {invoice && invoice.status !== "paid" && (
          <Card className="bg-white border border-slate-200 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Lock className="w-5 h-5 text-green-500" />
                Invoice Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Invoice Summary */}
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="font-mono font-semibold text-lg text-slate-900">
                      {invoice.invoice_number}
                    </p>
                    <p className="text-sm text-slate-600 mt-1">{invoice.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Amount Due</p>
                    <p className="text-2xl font-bold text-orange-500">
                      XCD ${amountDue.toFixed(2)}
                    </p>
                  </div>
                </div>
                <div className="flex justify-between text-sm border-t border-slate-200 pt-3">
                  <span className="text-slate-600">Total Invoice Amount</span>
                  <span className="font-medium">XCD ${invoice.amount.toFixed(2)}</span>
                </div>
                {invoice.amount_paid > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Amount Already Paid</span>
                    <span>-XCD ${invoice.amount_paid.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Due Date</span>
                  <span className="font-medium">{invoice.due_date}</span>
                </div>
              </div>

              {/* Payment Method Selection */}
              <div className="mb-6">
                <Label className="text-slate-700 mb-3 block">Payment Method</Label>
                <RadioGroup
                  value={paymentMethod}
                  onValueChange={setPaymentMethod}
                  className="space-y-3"
                >
                  <div
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      paymentMethod === "stripe"
                        ? "border-orange-500 bg-orange-50"
                        : "border-slate-200"
                    }`}
                    onClick={() => setPaymentMethod("stripe")}
                  >
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="stripe" id="stripe" />
                      <Label htmlFor="stripe" className="cursor-pointer flex-1">
                        <div className="flex items-center gap-3">
                          <CreditCard className="w-5 h-5 text-slate-600" />
                          <span className="font-medium">Credit / Debit Card</span>
                        </div>
                      </Label>
                    </div>
                  </div>
                  <div
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      paymentMethod === "paypal"
                        ? "border-orange-500 bg-orange-50"
                        : "border-slate-200"
                    }`}
                    onClick={() => setPaymentMethod("paypal")}
                  >
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="paypal" id="paypal" />
                      <Label htmlFor="paypal" className="cursor-pointer flex-1">
                        <div className="flex items-center gap-3">
                          <div className="w-5 h-5 bg-[#003087] rounded flex items-center justify-center">
                            <span className="text-white font-bold text-[8px]">PP</span>
                          </div>
                          <span className="font-medium">PayPal</span>
                        </div>
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {/* Payment Button */}
              <div>
                {paymentMethod === "stripe" ? (
                  <Button
                    onClick={handleStripePayment}
                    disabled={processing}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold h-12"
                    data-testid="pay-stripe-btn"
                  >
                    {processing ? (
                      "Processing..."
                    ) : (
                      <>
                        <CreditCard className="mr-2 w-5 h-5" />
                        Pay XCD ${amountDue.toFixed(2)}
                      </>
                    )}
                  </Button>
                ) : (
                  <div data-testid="paypal-buttons">
                    <PayPalCheckout
                      createOrder={createPayPalOrder}
                      onApprove={onPayPalApprove}
                      onCancel={() => toast.info("PayPal payment cancelled")}
                      onError={() => toast.error("PayPal payment failed")}
                      amount={amountDue}
                      currency="USD"
                    />
                  </div>
                )}
              </div>

              {/* Security Note */}
              <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
                <Shield className="w-4 h-4 text-green-500" />
                <span>Your payment is secure and encrypted</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Already Paid */}
        {invoice && invoice.status === "paid" && (
          <Card className="bg-white border border-slate-200 shadow-lg">
            <CardContent className="py-12 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">
                Invoice Already Paid
              </h2>
              <p className="text-slate-600">
                Invoice {invoice.invoice_number} has been fully paid. Thank you!
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
